export interface IconRailItem<Key extends string> {
  key: Key;
  label: string;
  icon: string;
  iconPath?: string;
}

export interface IconRailOptions<Key extends string> {
  ariaLabel: string;
  className?: string;
  itemClassName?: string;
  items: ReadonlyArray<IconRailItem<Key>>;
  activeKey: Key;
  role?: 'tablist' | 'navigation';
  buttonRole?: 'tab' | 'button';
  dataAttribute?: string;
  showLabels?: boolean;
  onSelect: (key: Key) => void;
}

export interface IconRail<Key extends string> {
  root: HTMLElement;
  buttons: ReadonlyMap<Key, HTMLButtonElement>;
  setActive(key: Key): void;
}

export function createIconRail<Key extends string>(opts: IconRailOptions<Key>): IconRail<Key> {
  injectIconRailStyles();

  const root = document.createElement('nav');
  root.className = ['dev-icon-rail', opts.className].filter(Boolean).join(' ');
  root.setAttribute('aria-label', opts.ariaLabel);
  root.setAttribute('role', opts.role ?? 'tablist');

  const buttons = new Map<Key, HTMLButtonElement>();

  for (const item of opts.items) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = ['dev-icon-rail-button', opts.itemClassName].filter(Boolean).join(' ');
    button.dataset.tooltip = item.label;
    button.setAttribute('aria-label', item.label);
    if (opts.buttonRole) button.setAttribute('role', opts.buttonRole);
    if (opts.dataAttribute) button.setAttribute(`data-${opts.dataAttribute}`, item.key);

    const icon = item.iconPath ? createSvgIcon(item.iconPath) : document.createElement('span');
    icon.classList.add('dev-icon-rail-glyph');
    icon.setAttribute('aria-hidden', 'true');
    if (!item.iconPath) icon.textContent = item.icon;

    const label = document.createElement('span');
    label.className = 'dev-icon-rail-label';
    if (opts.showLabels) label.classList.add('is-visible');
    label.textContent = item.label;

    button.append(icon, label);
    button.addEventListener('click', () => opts.onSelect(item.key));
    root.appendChild(button);
    buttons.set(item.key, button);
  }

  const setActive = (key: Key): void => {
    for (const [candidate, button] of buttons) {
      const active = candidate === key;
      button.classList.toggle('is-active', active);
      if (opts.buttonRole === 'tab') button.setAttribute('aria-selected', active ? 'true' : 'false');
      else button.setAttribute('aria-current', active ? 'page' : 'false');
    }
  };

  setActive(opts.activeKey);

  return { root, buttons, setActive };
}

function createSvgIcon(pathData: string): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.innerHTML = pathData;
  return svg;
}

function injectIconRailStyles(): void {
  if (document.getElementById('dev-icon-rail-styles')) return;
  const style = document.createElement('style');
  style.id = 'dev-icon-rail-styles';
  style.textContent = `
    .dev-icon-rail {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
      padding: 6px 4px;
      box-sizing: border-box;
    }
    .dev-icon-rail-button {
      appearance: none;
      width: 42px;
      min-width: 42px;
      min-height: 40px;
      border: 1px solid rgba(126, 211, 255, 0.2);
      border-radius: 4px;
      background: rgba(9, 18, 28, 0.72);
      color: rgba(217, 243, 255, 0.78);
      cursor: pointer;
      display: grid;
      place-items: center;
      gap: 3px;
      padding: 0;
      position: relative;
      transition: background 80ms ease, border-color 80ms ease, color 80ms ease, box-shadow 80ms ease;
    }
    .dev-icon-rail-button[data-tooltip]::after {
      content: attr(data-tooltip);
      position: absolute;
      left: calc(100% + 8px);
      top: 50%;
      transform: translateY(-50%);
      padding: 4px 8px;
      background: rgba(8, 14, 22, 0.96);
      color: #f1f9ff;
      border: 1px solid rgba(126, 211, 255, 0.45);
      border-radius: 3px;
      font: 600 11px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      transition: opacity 80ms ease;
      z-index: 10;
    }
    .dev-icon-rail-button:hover[data-tooltip]::after,
    .dev-icon-rail-button:focus-visible[data-tooltip]::after {
      opacity: 1;
      visibility: visible;
      transition-delay: 120ms;
    }
    .dev-icon-rail-button:hover,
    .dev-icon-rail-button:focus-visible {
      border-color: rgba(126, 211, 255, 0.62);
      color: #ffffff;
      outline: none;
    }
    .dev-icon-rail-button.is-active {
      border-color: rgba(255, 180, 74, 0.82);
      background: rgba(255, 180, 74, 0.14);
      color: #ffcf86;
      box-shadow: inset 0 0 0 1px rgba(255, 180, 74, 0.12);
    }
    .dev-icon-rail-glyph {
      font: 800 10px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
      letter-spacing: 0;
      text-transform: uppercase;
      width: 20px;
      height: 20px;
    }
    .dev-icon-rail-label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
    .dev-icon-rail-label.is-visible {
      position: static;
      width: auto;
      height: auto;
      margin: 0;
      overflow: visible;
      clip: auto;
      white-space: normal;
      color: currentColor;
      font: 700 9px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
      text-transform: none;
    }
  `;
  document.head.appendChild(style);
}
